import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { StatusBadge } from '../../_shared/components/StatusBadge';
import { formatGatewayDate } from '../../_shared/utils/format';
import { useRequestLogs } from '../hooks/useRequestLogs';
import type { RequestLogDto, RequestLogFilters } from '../types/requestLog';

// Skeleton loader — mirrors sibling pages
function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: '48px',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

/** Map HTTP status code to StatusBadge variant */
function statusKind(status: number | null): 'active' | 'pending' | 'deprecated' | 'disabled' | 'unhealthy' {
  if (status === null) return 'disabled';
  if (status >= 200 && status < 300) return 'active';
  if (status >= 300 && status < 400) return 'pending';
  if (status >= 400 && status < 500) return 'deprecated';
  return 'unhealthy';
}

/** Truncate long strings for table display */
function truncate(s: string | null | undefined, len = 18): string {
  if (!s) return '—';
  return s.length > len ? s.slice(0, len) + '…' : s;
}

export function RequestLogsPage() {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<RequestLogFilters>({});

  // Local form state — applied on "Search" click to avoid re-fetching on every keystroke
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [statusClass, setStatusClass] = useState<RequestLogFilters['statusClass']>('');
  const [statusInput, setStatusInput] = useState('');
  const [routeId, setRouteId] = useState('');
  const [ip, setIp] = useState('');
  const [userId, setUserId] = useState('');
  const [traceId, setTraceId] = useState('');

  const applyFilters = useCallback(() => {
    setFilters({
      from: from || undefined,
      to: to || undefined,
      statusClass: (statusClass as RequestLogFilters['statusClass']) || undefined,
      status: statusInput || undefined,
      routeId: routeId || undefined,
      ip: ip || undefined,
      userId: userId || undefined,
      traceId: traceId || undefined,
    });
  }, [from, to, statusClass, statusInput, routeId, ip, userId, traceId]);

  const resetFilters = useCallback(() => {
    setFrom('');
    setTo('');
    setStatusClass('');
    setStatusInput('');
    setRouteId('');
    setIp('');
    setUserId('');
    setTraceId('');
    setFilters({});
  }, []);

  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRequestLogs(filters);

  // Flatten all pages into one row array
  const rows: RequestLogDto[] = data?.pages.flatMap((page) => page.content) ?? [];

  // Detect 403 forbidden
  const isForbidden =
    isError &&
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code?: string }).code === 'GW-AUTH-403-001';

  const columns: DataTableColumn<RequestLogDto>[] = [
    {
      key: 'timestamp',
      header: t('gateway.requestLogs.columns.timestamp'),
      width: '140px',
      render: (row) => (
        <span
          title={row.timestamp}
          style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}
        >
          {formatGatewayDate(row.timestamp)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('gateway.requestLogs.columns.status'),
      width: '90px',
      render: (row) => (
        <StatusBadge
          status={statusKind(row.status)}
          label={row.status !== null ? String(row.status) : '—'}
          size="sm"
        />
      ),
    },
    {
      key: 'routeId',
      header: t('gateway.requestLogs.columns.routeId'),
      width: '180px',
      render: (row) => (
        <span
          style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}
          title={row.routeId ?? undefined}
        >
          {truncate(row.routeId, 16)}
        </span>
      ),
    },
    {
      key: 'latencyMs',
      header: t('gateway.requestLogs.columns.latencyMs'),
      width: '100px',
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)' }}>
          {row.latencyMs !== null ? `${row.latencyMs} ms` : '—'}
        </span>
      ),
    },
    {
      key: 'ip',
      header: t('gateway.requestLogs.columns.ip'),
      width: '130px',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
          {row.ip || '—'}
        </span>
      ),
    },
    {
      key: 'userId',
      header: t('gateway.requestLogs.columns.userId'),
      width: '130px',
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)' }}>
          {truncate(row.userId, 16)}
        </span>
      ),
    },
    {
      key: 'traceId',
      header: t('gateway.requestLogs.columns.traceId'),
      width: '180px',
      render: (row) => (
        <span
          style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}
          title={row.traceId}
        >
          <button
            type="button"
            aria-label={t('gateway.requestLogs.actions.copyTraceId')}
            onClick={() => navigator.clipboard.writeText(row.traceId)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-primary)',
            }}
          >
            {truncate(row.traceId, 14)}
          </button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('gateway.requestLogs.title')}
        breadcrumb={t('gateway.requestLogs.breadcrumb')}
      />

      {/* Filter Bar */}
      <div
        role="search"
        aria-label={t('gateway.requestLogs.filter.ariaLabel')}
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-4) var(--spacing-6)',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          background: 'var(--color-surface-muted)',
          borderBottom: '1px solid var(--color-surface-border)',
        }}
      >
        {/* Time range */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '1 1 180px', minWidth: '160px' }}>
          <label
            htmlFor="rl-from"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.from')}
          </label>
          <input
            id="rl-from"
            type="datetime-local"
            aria-label={t('gateway.requestLogs.filter.from')}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '1 1 180px', minWidth: '160px' }}>
          <label
            htmlFor="rl-to"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.to')}
          </label>
          <input
            id="rl-to"
            type="datetime-local"
            aria-label={t('gateway.requestLogs.filter.to')}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Status Class */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '0 1 130px', minWidth: '120px' }}>
          <label
            htmlFor="rl-status-class"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.statusClass')}
          </label>
          <select
            id="rl-status-class"
            aria-label={t('gateway.requestLogs.filter.statusClass')}
            value={statusClass ?? ''}
            onChange={(e) => setStatusClass(e.target.value as RequestLogFilters['statusClass'])}
            style={selectStyle}
          >
            <option value="">{t('gateway.requestLogs.filter.allStatusClasses')}</option>
            <option value="2xx">2xx</option>
            <option value="3xx">3xx</option>
            <option value="4xx">4xx</option>
            <option value="5xx">5xx</option>
          </select>
        </div>

        {/* Exact status */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '0 1 100px', minWidth: '90px' }}>
          <label
            htmlFor="rl-status"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.status')}
          </label>
          <input
            id="rl-status"
            type="number"
            aria-label={t('gateway.requestLogs.filter.status')}
            placeholder="200"
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value)}
            style={inputStyle}
            min={100}
            max={599}
          />
        </div>

        {/* RouteId */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '1 1 160px', minWidth: '140px' }}>
          <label
            htmlFor="rl-route-id"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.routeId')}
          </label>
          <input
            id="rl-route-id"
            type="search"
            aria-label={t('gateway.requestLogs.filter.routeId')}
            placeholder="uuid"
            value={routeId}
            onChange={(e) => setRouteId(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* IP */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '0 1 140px', minWidth: '120px' }}>
          <label
            htmlFor="rl-ip"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.ip')}
          </label>
          <input
            id="rl-ip"
            type="search"
            aria-label={t('gateway.requestLogs.filter.ip')}
            placeholder="1.2.3.*"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* UserId */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '0 1 140px', minWidth: '120px' }}>
          <label
            htmlFor="rl-user-id"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.userId')}
          </label>
          <input
            id="rl-user-id"
            type="search"
            aria-label={t('gateway.requestLogs.filter.userId')}
            placeholder={t('gateway.requestLogs.filter.userIdPlaceholder')}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* TraceId */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '1 1 180px', minWidth: '160px' }}>
          <label
            htmlFor="rl-trace-id"
            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            {t('gateway.requestLogs.filter.traceId')}
          </label>
          <input
            id="rl-trace-id"
            type="search"
            aria-label={t('gateway.requestLogs.filter.traceId')}
            placeholder="trace-id"
            value={traceId}
            onChange={(e) => setTraceId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            style={inputStyle}
          />
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-end' }}>
          <button
            type="button"
            onClick={applyFilters}
            style={primaryBtnStyle}
          >
            {t('gateway.requestLogs.filter.search')}
          </button>
          <button
            type="button"
            onClick={resetFilters}
            style={secondaryBtnStyle}
          >
            {t('gateway.requestLogs.filter.reset')}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: 'var(--spacing-4) var(--spacing-6) var(--spacing-6)' }}>
        {isLoading ? (
          <TableSkeleton />
        ) : isForbidden ? (
          <ErrorState
            title={t('gateway.shared.error.forbidden')}
            message={t('gateway.shared.error.forbiddenMessage')}
          />
        ) : isError ? (
          <ErrorState
            title={t('gateway.requestLogs.list.error')}
            message={t('gateway.shared.error.message')}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('gateway.requestLogs.list.empty')}
            description={t('gateway.requestLogs.list.emptyDesc')}
          />
        ) : (
          <>
            {/* Result count */}
            <div
              style={{
                marginBottom: 'var(--spacing-3)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('gateway.requestLogs.list.resultCount', { count: rows.length })}
              {hasNextPage && (
                <span style={{ marginLeft: 'var(--spacing-2)' }}>
                  {t('gateway.requestLogs.list.moreAvailable')}
                </span>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
              />
            </div>

            {/* Load More button */}
            {hasNextPage && (
              <div
                style={{
                  marginTop: 'var(--spacing-4)',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <button
                  type="button"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  aria-label={t('gateway.requestLogs.list.loadMoreAriaLabel')}
                  style={{
                    ...secondaryBtnStyle,
                    minWidth: '160px',
                    opacity: isFetchingNextPage ? 0.6 : 1,
                  }}
                >
                  {isFetchingNextPage
                    ? t('gateway.requestLogs.list.loading')
                    : t('gateway.requestLogs.list.loadMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Style constants — design-token vars only, no hardcoded colors
const primaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  height: '36px',
  whiteSpace: 'nowrap',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  height: '36px',
  whiteSpace: 'nowrap',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  height: '36px',
  width: '100%',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  height: '36px',
  width: '100%',
};
